import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, Card, Form, Input, Skeleton, Typography } from 'antd';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../lib/api-client';
import {
  fetchCompany, saveCompany, type CompanyProfile, type UpdateCompanyInput,
} from './api';

const { Title, Paragraph } = Typography;

type FormValues = { [K in keyof CompanyProfile]: string };

const toForm = (c: CompanyProfile): FormValues => ({
  legalName: c.legalName,
  registrationNo: c.registrationNo ?? '',
  tin: c.tin ?? '',
  email: c.email ?? '',
  website: c.website ?? '',
  documentFooter: c.documentFooter ?? '',
});

/** Company details: the legal entity printed on every invoice and receipt. */
export function CompanyPage(): ReactNode {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<FormValues>();

  const company = useQuery({ queryKey: ['iam', 'company'], queryFn: fetchCompany });

  const save = useMutation({
    mutationFn: saveCompany,
    onSuccess: (saved) => {
      message.success(t('iam.company.saved'));
      queryClient.setQueryData(['iam', 'company'], saved);
      form.setFieldsValue(toForm(saved));
    },
    onError: (e: unknown) => {
      message.error(e instanceof ApiError ? e.message : t('iam.genericError'));
    },
  });

  if (company.isError) {
    const denied = company.error instanceof ApiError && company.error.status === 403;
    return <Alert type="error" message={t(denied ? 'iam.company.noAccess' : 'iam.company.loadFailed')} />;
  }
  if (company.data === undefined) return <Skeleton active />;

  const saved = toForm(company.data);

  const submit = (values: FormValues): void => {
    const changed: UpdateCompanyInput = {};
    for (const key of Object.keys(saved) as (keyof FormValues)[]) {
      const next = values[key].trim();
      if (next !== saved[key]) changed[key] = next;
    }
    if (Object.keys(changed).length === 0) return;
    save.mutate(changed);
  };

  return (
    <Card style={{ maxWidth: 640 }}>
      <Title level={4}>{t('iam.company.title')}</Title>
      <Paragraph type="secondary">{t('iam.company.consequence')}</Paragraph>
      <Form form={form} layout="vertical" initialValues={saved} onFinish={submit}>
        <Form.Item
          name="legalName"
          label={t('iam.company.legalName')}
          rules={[{ required: true, whitespace: true, min: 2, max: 160, message: t('iam.company.legalNameRequired') }]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="registrationNo" label={t('iam.company.registrationNo')} rules={[{ max: 40 }]}>
          <Input />
        </Form.Item>
        <Form.Item name="tin" label={t('iam.company.tin')} rules={[{ max: 20 }]}>
          <Input />
        </Form.Item>
        <Form.Item
          name="email"
          label={t('iam.company.email')}
          rules={[{ type: 'email', message: t('iam.company.emailInvalid') }, { max: 120 }]}
        >
          <Input inputMode="email" />
        </Form.Item>
        <Form.Item name="website" label={t('iam.company.website')} rules={[{ max: 160 }]}>
          <Input />
        </Form.Item>
        <Form.Item name="documentFooter" label={t('iam.company.documentFooter')} rules={[{ max: 500 }]}>
          <Input.TextArea rows={3} />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={save.isPending}>
          {t('iam.company.save')}
        </Button>
      </Form>
    </Card>
  );
}
